import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';

import { ProductLoaderOrigin } from '../domain/enums/product-loader-origin.enum';
import { ProductLoaderService } from '../services/product-loader-service.impl';

import {
    LoadProductsAction,
    LoadProductsErrorAction,
    LoadProductsSuccessAction,
    LoadProductsWithIdsAction,
    LoadProductsWithProductsAction,
} from './product.actions';

import { getCmsConfigValue, getContext } from '@core/infrastructure/state/core.selectors';
import { ProductsArrayFeaturePublicApi, ProductsArrayPublicFunctions } from '@features/products/productsArray';
import { ItxObservabilityCommands } from '@frontend/libs/itx-observability-core';

@Injectable()
export class ProductEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly productLoaderService: ProductLoaderService,
        private readonly store: Store,
        private readonly productsArrayPublicApi: ProductsArrayFeaturePublicApi,
    ) { }

    public loadProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadProductsAction),
            map((data) => {
                if (this.store.selectSignal(getCmsConfigValue('GET_CATEGORY_WITH_PRODUCTS'))()) {
                    return LoadProductsWithProductsAction(data);
                }

                return LoadProductsWithIdsAction(data);
            }),
        );
    });

    public loadProductsWithProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadProductsWithProductsAction),
            withLatestFrom(this.store.select(getContext)),
            map(([data, context]) => {
                const productsIds = data.ids?.split(',');
                const products = { products: this.productLoaderService.getApiProductsList(productsIds) };
                const productsArrayContext = ProductsArrayPublicFunctions.transformContextToProductsArrayContext(context);
                // eslint-disable-next-line max-len
                const transformedProducts = ProductsArrayPublicFunctions.transformProductsFromApi(products, data.categoryId, productsArrayContext);
                this.productLoaderService.addProductsToList(transformedProducts, ProductLoaderOrigin.CATEGORY);
                return LoadProductsSuccessAction();
            }),
        );
    });

    public loadProductsWithIds$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadProductsWithIdsAction),
            mergeMap((data) => {
                return this.productsArrayPublicApi.getProductsArray$(data.ids, data.categoryId).pipe(
                    tap(products => {
                        this.productLoaderService.addProductsToList(products, ProductLoaderOrigin.CATEGORY);
                    })
                );
            }),
            map(() => {
                try {
                    return LoadProductsSuccessAction();
                } catch (error) {
                    ItxObservabilityCommands.sendObservabilityLog(error, 'loadProducts$');
                    return LoadProductsErrorAction();
                }
            }),
        );
    });
}
